import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CalendarDays, Clock3, MapPin, Search } from 'lucide-react';
import { PublicClubEvent, PublicClubProfile, clubsBookingApi } from '@/api/clubs-booking';
import PublicClubHeader from '@/components/public/PublicClubHeader';
import ParentPortalShell from '@/components/parent/ParentPortalShell';
import { auth } from '@/store/authStore';

const getActivityLabel = (title: string) => {
  const parts = title.split(' - ').map((part) => part.trim()).filter(Boolean);
  if (parts.length >= 2) return parts.slice(1).join(' - ');
  return title.trim();
};

const getVenueLabel = (location: string) => {
  const first = location.split(',')[0]?.trim();
  return first || location;
};

const formatDateRange = (startDate: string, endDate: string) => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  return `${start.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  })} - ${end.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  })}`;
};

const formatTimeRange = (startDate: string, endDate: string) => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  return `${start.toLocaleTimeString('en-GB', {
    hour: 'numeric',
    minute: '2-digit',
  })} - ${end.toLocaleTimeString('en-GB', {
    hour: 'numeric',
    minute: '2-digit',
  })}`;
};

type PublicClubEventsPageProps = {
  clubSlugOverride?: string;
};

const PublicClubEventsPage = ({ clubSlugOverride }: PublicClubEventsPageProps) => {
  const { clubSlug: routeClubSlug } = useParams<{ clubSlug: string }>();
  const clubSlug = (clubSlugOverride || routeClubSlug || '').trim();
  const [searchParams] = useSearchParams();
  const [clubProfile, setClubProfile] = useState<PublicClubProfile | null>(null);
  const [events, setEvents] = useState<PublicClubEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVenue, setSelectedVenue] = useState('all');
  const [selectedActivity, setSelectedActivity] = useState('all');
  const [selectedTab, setSelectedTab] = useState('all');

  useEffect(() => {
    const load = async () => {
      if (!clubSlug) return;
      setLoading(true);
      setError('');
      try {
        const response = await clubsBookingApi.getPublicClubEvents(clubSlug);
        setClubProfile(response.clubProfile);
        setEvents(response.items || []);
      } catch (err: any) {
        const messageText = err?.response?.data?.message;
        setError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Could not load this club page');
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [clubSlug]);

  const venueOptions = useMemo(() => {
    const values = Array.from(new Set(events.map((event) => getVenueLabel(event.location))));
    return values.sort((a, b) => a.localeCompare(b));
  }, [events]);

  const activityOptions = useMemo(() => {
    const values = Array.from(new Set(events.map((event) => getActivityLabel(event.title))));
    return values.sort((a, b) => a.localeCompare(b));
  }, [events]);

  const filteredEvents = useMemo(() => {
    const lowered = searchTerm.trim().toLowerCase();

    return events.filter((event) => {
      const activity = getActivityLabel(event.title);
      const venue = getVenueLabel(event.location);

      if (selectedTab !== 'all' && activity !== selectedTab) return false;
      if (selectedVenue !== 'all' && venue !== selectedVenue) return false;
      if (selectedActivity !== 'all' && activity !== selectedActivity) return false;
      if (!lowered) return true;

      return (
        event.title.toLowerCase().includes(lowered) ||
        event.location.toLowerCase().includes(lowered) ||
        activity.toLowerCase().includes(lowered)
      );
    });
  }, [events, searchTerm, selectedVenue, selectedActivity, selectedTab]);

  const isParentLoggedIn = (() => {
    const user = auth.getUser();
    return !!(user.isAuthenticated && user.role === 'parent');
  })();
  const showBackLink = !clubSlugOverride && searchParams.get('showBack') === 'true';

  if (loading) {
    return <main className="min-h-screen bg-[#eef0f4] p-6">Loading activities...</main>;
  }

  if (error && !clubProfile) {
    return <main className="min-h-screen bg-[#eef0f4] p-6 text-red-700">{error}</main>;
  }

  if (!clubProfile) {
    return <main className="min-h-screen bg-[#eef0f4] p-6">Club page not found.</main>;
  }

  const content = (
      <div className="w-full px-3 py-8 sm:px-4 md:px-8">
        <div className="mb-4">
          {showBackLink && (
            <Link
              to="/clubs"
              className="inline-flex items-center gap-2 text-sm font-medium text-[#1F8F5F] hover:underline"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to clubs
            </Link>
          )}
          {clubProfile.description && (
            <p className={`${showBackLink ? 'mt-3' : ''} max-w-3xl text-sm text-slate-600`}>{clubProfile.description}</p>
          )}
        </div>

        <div className="grid gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
        <aside className="h-fit min-w-0 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-4xl font-semibold text-slate-900">Filter</h2>

          <label className="mt-6 block">
            <span className="sr-only">Search by title</span>
            <div className="flex items-center gap-2 rounded-2xl border border-slate-300 px-3 py-2.5">
              <Search className="h-5 w-5 text-slate-400" />
              <input
                placeholder="Search by title"
                className="w-full border-0 bg-transparent text-lg text-slate-700 outline-none"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </label>

          <label className="mt-6 block text-lg text-slate-700">
            Venue
            <select
              className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-lg"
              value={selectedVenue}
              onChange={(e) => setSelectedVenue(e.target.value)}
            >
              <option value="all">Any</option>
              {venueOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>

          <label className="mt-5 block text-lg text-slate-700">
            Activity
            <select
              className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-lg"
              value={selectedActivity}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedActivity(value);
                setSelectedTab(value === 'all' ? 'all' : value);
              }}
            >
              <option value="all">Any</option>
              {activityOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
        </aside>

        <section className="min-w-0">
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex min-w-max">
              <button
                type="button"
                onClick={() => {
                  setSelectedTab('all');
                  setSelectedActivity('all');
                }}
                className={`min-w-[132px] border-r border-slate-200 px-5 py-4 text-lg font-medium transition sm:min-w-[180px] sm:px-8 ${
                  selectedTab === 'all' ? 'border-b-2 border-b-[#1F8F5F] text-[#1F8F5F]' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                All
              </button>
              {activityOptions.map((activity) => (
                <button
                  key={activity}
                  type="button"
                  onClick={() => {
                    setSelectedTab(activity);
                    setSelectedActivity(activity);
                  }}
                  className={`min-w-[180px] border-r border-slate-200 px-5 py-4 text-lg font-medium transition sm:min-w-[220px] sm:px-8 ${
                    selectedTab === activity
                      ? 'border-b-2 border-b-[#1F8F5F] text-[#1F8F5F]'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {activity}
                </button>
              ))}
            </div>
          </div>

          {error && <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}

          <div className="mt-6 grid gap-5 md:grid-cols-2">
            {filteredEvents.map((event) => (
              <article key={event._id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h3 className="break-words text-[34px] leading-tight text-[#1F8F5F]">
                  <Link to={`/book/${event._id}`} className="hover:underline">
                    {event.title}
                  </Link>
                </h3>

                <div className="my-4 border-b border-slate-200" />

                <div className="space-y-3 text-slate-600">
                  <div className="flex items-center gap-2 text-lg">
                    <CalendarDays className="h-5 w-5 text-slate-400" />
                    <span>{formatDateRange(event.startDate, event.endDate)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-lg">
                    <Clock3 className="h-5 w-5 text-slate-400" />
                    <span>{formatTimeRange(event.startDate, event.endDate)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-lg">
                    <MapPin className="h-5 w-5 text-slate-400" />
                    <span>{getVenueLabel(event.location)}</span>
                  </div>
                  {!!event.sessions?.length && (
                    <div className="text-sm text-slate-500">
                      {event.sessions.length} session{event.sessions.length === 1 ? '' : 's'} available
                    </div>
                  )}
                </div>

                <div className="mt-4 flex items-center justify-between gap-3">
                  <span
                    className={`rounded-full px-3 py-1 text-sm font-semibold ${
                      (event.capacity < 0 || event.spotsRemaining > 0) ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                    }`}
                  >
                    {(event.capacity < 0 || event.spotsRemaining > 0) ? 'Places available' : 'Fully booked'}
                  </span>

                  <Link
                    to={`/book/${event._id}`}
                    className="rounded-lg border border-[#D8F1E2] bg-[#F3FBF7] px-3 py-1.5 text-sm font-medium text-[#1F8F5F] hover:bg-[#EAF7F0]"
                  >
                    Book now
                  </Link>
                </div>
              </article>
            ))}
          </div>

          {filteredEvents.length === 0 && (
            <div className="mt-6 rounded-2xl border border-slate-200 bg-white px-5 py-6 text-slate-500">
              No events match your filters.
            </div>
          )}

          {clubProfile.bookingTerms && (
            <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-600">
              <h4 className="text-base font-semibold text-slate-900">Booking terms</h4>
              <p className="mt-2">{clubProfile.bookingTerms}</p>
            </div>
          )}
        </section>
        </div>
      </div>
  );

  if (isParentLoggedIn) {
    return (
      <ParentPortalShell title={clubProfile.clubName} subtitle="Browse and book activities" showHeaderCard={false}>
        {content}
      </ParentPortalShell>
    );
  }

  return (
    <main className="min-h-screen overflow-x-clip bg-[#eef0f4] text-slate-800">
      <PublicClubHeader
        clubName={clubProfile.clubName}
        clubSlug={clubProfile.publicSlug}
        clubDescription={clubProfile.description}
        logoUrl={clubProfile.logoUrl}
        showClubTitle={clubProfile.showClubTitle !== false}
        cartHref="/cart"
      />
      {content}
    </main>
  );
};

export default PublicClubEventsPage;



